import React, { useEffect, useState } from 'react';
import { Doughnut, Line } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';

import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, annotationPlugin);

export default function FearAndGreed({ compact = false }) {
  const navigate = useNavigate();
  const [index, setIndex] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/fear-and-greed/history')
      .then(async res => {
        if (res.status === 204) {
          throw new Error('Aucune donnée disponible');
        }
        if (!res.ok) throw new Error('Erreur serveur');
        const text = await res.text();
        if (!text) throw new Error('Aucune donnée reçue');
        try {
          return JSON.parse(text);
        } catch {
          throw new Error('Réponse invalide');
        }
      })
      .then(data => {
        if (data && data.data && data.data.length > 0) {
          setHistory(data.data.slice().reverse().map(d => ({
            value: Number(d.value),
            timestamp: d.timestamp,
            classification: d.value_classification
          })));
          setIndex(data.data[0]);
        } else {
          setError('Aucune donnée historique');
        }
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  const handleClick = () => {
    if (compact) {
      navigate('/fear-and-greed');
    }
  };

  // Compact mode for Portfolio
  if (compact) {
    if (loading) {
      return (
        <div style={{
          background: 'var(--color-surface, #ffffff)',
          color: 'var(--color-text-main, #333)',
          borderRadius: 12,
          padding: '1.5rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid var(--color-border, #e0e0e0)',
          textAlign: 'center'
        }}>
          Chargement...
        </div>
      );
    }

    if (error || !index) {
      return (
        <div style={{
          background: 'var(--color-surface, #ffffff)',
          color: '#e74c3c',
          borderRadius: 12,
          padding: '1.5rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid var(--color-border, #e0e0e0)',
          textAlign: 'center'
        }}>
          {error || 'Aucune donnée'}
        </div>
      );
    }

    return (
      <div
        onClick={handleClick}
        style={{
          background: 'var(--color-surface, #ffffff)',
          color: 'var(--color-text-main, #333)',
          borderRadius: 12,
          padding: '1.5rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid var(--color-border, #e0e0e0)',
          cursor: 'pointer',
          transition: 'transform 0.2s, box-shadow 0.2s',
          userSelect: 'none',
          WebkitTapHighlightColor: 'transparent'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-2px)';
          e.currentTarget.style.boxShadow = '0 6px 24px rgba(0, 0, 0, 0.12)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
        }}
      >
        <div style={{
          fontSize: '1.1rem',
          fontWeight: '600',
          color: 'var(--color-text-main, #333)',
          marginBottom: '1rem',
          textAlign: 'center'
        }}>
          Fear & Greed Index
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
          {/* Jauge compacte */}
          <div style={{ width: 160, height: 160 }}>
            <Doughnut
              data={{
                labels: ['Index', 'Max'],
                datasets: [{
                  data: [Number(index.value), 100 - Number(index.value)],
                  backgroundColor: [
                    Number(index.value) < 30 ? '#ef4444' : Number(index.value) < 60 ? '#f59e42' : '#22c55e',
                    '#e5e7eb'
                  ],
                  borderWidth: 2
                }]
              }}
              options={{
                cutout: '70%',
                circumference: 180,
                rotation: 270,
                plugins: {
                  legend: { display: false },
                  tooltip: { enabled: false }
                }
              }}
            />
            <div style={{
              position: 'relative',
              top: '-80px',
              textAlign: 'center',
              fontSize: '2.5rem',
              fontWeight: '700',
              color: Number(index.value) < 30 ? '#ef4444' : Number(index.value) < 60 ? '#f59e42' : '#22c55e'
            }}>
              {index.value}
            </div>
          </div>
          
          <div style={{
            textAlign: 'center',
            fontSize: '1rem',
            fontWeight: '600',
            marginTop: '-60px',
            padding: '0.4rem 0.8rem',
            borderRadius: 6,
            background: Number(index.value) < 30 ? 'rgba(239, 68, 68, 0.1)' : Number(index.value) < 60 ? 'rgba(245, 158, 66, 0.1)' : 'rgba(34, 197, 94, 0.1)',
            color: Number(index.value) < 30 ? '#ef4444' : Number(index.value) < 60 ? '#f59e42' : '#22c55e'
          }}>
            {index.value_classification}
          </div>

          <div style={{ fontSize: '0.8rem', color: '#999', marginTop: '0.5rem' }}>
            Cliquez pour voir l'historique
          </div>
        </div>
      </div>
    );
  }

  // Full mode (default)
  return (
    <div style={{ color: 'var(--color-text-main)', minHeight: '400px', padding: '1.5rem', borderRadius: 8, boxShadow: '0 2px 8px var(--color-card-shadow)' }}>
      <h2>Fear and Greed Index</h2>
      {loading && <div>Chargement...</div>}
      {error && <div style={{ color: 'red' }}>{error}</div>}
      {index && (
        <>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 32, flexWrap: 'wrap' }}>
            {/* Jauge (doughnut) */}
            <div style={{ width: 180, height: 180 }}>
              <Doughnut
                data={{
                  labels: ['Index', 'Max'],
                  datasets: [{
                    data: [Number(index.value), 100 - Number(index.value)],
                    backgroundColor: [
                      Number(index.value) < 30 ? '#ef4444' : Number(index.value) < 60 ? '#f59e42' : '#22c55e',
                      '#e5e7eb'
                    ],
                    borderWidth: 2
                  }]
                }}
                options={{
                  cutout: '70%',
                  circumference: 180,
                  rotation: 270,
                  plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false }
                  }
                }}
              />
              <div style={{
                position: 'relative',
                top: '-90px', // Descendre le nombre pour éviter l'arc
                textAlign: 'center',
                fontSize: 32,
                fontWeight: 700
              }}>{index.value}</div>
              <div style={{ textAlign: 'center', fontSize: 16, fontWeight: 400, marginTop: -16 }}>
                {index.value_classification}
              </div>
            </div>
            {/* Historique */}
            {history.length > 1 && (
              <div style={{ flex: 1, minWidth: 260 }}>
                <Line
                  data={{
                    labels: history.map(d => new Date(Number(d.timestamp) * 1000).toLocaleDateString()),
                    datasets: [{
                      label: 'Index',
                      data: history.map(d => d.value),
                      fill: false,
                      // borderColor will be set per-segment below
                      backgroundColor: '#3b82f6',
                      tension: 0.2,
                      pointRadius: 3,
                      pointBackgroundColor: history.map(d => d.value < 30 ? '#ef4444' : d.value < 60 ? '#f59e42' : '#22c55e'),
                      segment: {
                        borderColor: ctx => {
                          const { p0, p1 } = ctx;
                          if (!p0 || !p1 || !p0.parsed || !p1.parsed || typeof p0.parsed.y !== 'number' || typeof p1.parsed.y !== 'number') return '#3b82f6';
                          const avg = (p0.parsed.y + p1.parsed.y) / 2;
                          if (avg < 30) return '#ef4444';
                          if (avg < 60) return '#f59e42';
                          return '#22c55e';
                        }
                      }
                    }]
                  }}
                  options={{
                    responsive: true,
                    plugins: {
                      legend: { display: false },
                      annotation: {
                        annotations: {
                          zoneFear: {
                            type: 'box',
                            yMin: 0,
                            yMax: 30,
                            backgroundColor: 'rgba(239,68,68,0.10)',
                            borderWidth: 0,
                          },
                          zoneNeutral: {
                            type: 'box',
                            yMin: 30,
                            yMax: 60,
                            backgroundColor: 'rgba(245,158,66,0.10)',
                            borderWidth: 0,
                          },
                          zoneGreed: {
                            type: 'box',
                            yMin: 60,
                            yMax: 100,
                            backgroundColor: 'rgba(34,197,94,0.10)',
                            borderWidth: 0,
                          }
                        }
                      }
                    },
                    scales: {
                      y: { min: 0, max: 100 }
                    }
                  }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 4 }}>
                  <span style={{ color: '#ef4444' }}>Peur</span>
                  <span style={{ color: '#f59e42' }}>Neutre</span>
                  <span style={{ color: '#22c55e' }}>Avidité</span>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
